<template>
    <transition name="fade">
        <div
            class="fixed inset-0 flex items-center justify-center bg-slate-800 bg-opacity-50 backdrop-blur-sm"
            style="z-index: 9999"
            role="dialog"
            aria-labelledby="modal-title"
            :aria-hidden="isOpen"
            v-if="isOpen"
        >
            <div
                class="w-full relative flex flex-col justify-center items-center"
                :style="{ ...modalStyle, ...maxWidth, ...maxHeight }"
            >
                <div
                    class="flex flex-col w-[95%] lg:w-full min-h-28 max-h-[95vh] relative bg-white rounded-lg shadow-xl"
                >
                    <div class="ui-modal-header pr-10">
                        <slot name="title">
                            <div class="flex items-center">
                                <div
                                    class="w-12 h-12 rounded-full flex flex-col justify-center items-center mr-3"
                                    :class="`bg-${iconColor}-100`"
                                    v-if="icon"
                                >
                                    <Icon
                                        :icon="icon"
                                        :path-class="`fill-${iconColor}-500`"
                                    />
                                </div>
                                <h5
                                    id="modal-title"
                                    class="ui-modal-title"
                                    v-if="title"
                                >
                                    {{ title }}
                                </h5>
                            </div>
                        </slot>
                        <slot name="close">
                            <button
                                type="button"
                                class="z-10 w-10 rounded-lg h-10 justify-center items-center absolute right-5 top-4 focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200"
                                @click="handleClose"
                                tabindex="1"
                            >
                                <icon
                                    icon="x"
                                    path-class="stroke-slate-500 hover:stroke-slate-700"
                                    svg-class="fill-slate-500 hover:fill-slate-700 h-5 w-5"
                                />
                                <span class="sr-only">Close</span>
                            </button>
                        </slot>
                    </div>
                    <!-- slot for modal contents: -->
                    <div class="ui-modal-content overflow-y-auto">
                        <slot name="content"></slot>
                    </div>
                    <div class="ui-modal-errors">
                        <slot name="errors"></slot>
                    </div>
                    <!-- slot for modal footer: -->
                    <div class="ui-modal-footer">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>
<script>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAppStore } from '@/store/app';
import Icon from '@/components/ui/icon/icon.vue';
import Errors from '@/components/errors.vue';

export default {
    emits: ['close'],
    components: {
        Icon,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: null,
        },
        icon: {
            type: String,
            default: null,
        },
        iconColor: {
            type: String,
            default: 'slate',
        },
        width: {
            type: [String, Number],
            default: '800px',
        },
        hasAutoflow: {
            type: Boolean,
            default: true,
        },
        errors: {
            type: Array,
            default: () => [],
        },
        isTeleported: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const viewportWidth = ref(window.innerWidth);
        const viewportHeight = ref(window.innerHeight);

        const appStore = useAppStore();
        const { sidebarOpen } = storeToRefs(appStore);

        const maxWidth = computed(() => {
            return { maxWidth: `${props.width}` };
        });

        const maxHeight = { maxHeight: '95dvh' };

        const modalStyle = computed(() => {
            const sidebarWidth = sidebarOpen.value ? '128px' : '0px';

            return {
                transform: props.isTeleported
                    ? 'translateX(0)'
                    : `translateX(${sidebarWidth})`,
            };
        });

        const handleClose = () => {
            emit('close');
        };

        const updateViewportSize = () => {
            viewportWidth.value = window.innerWidth;
            viewportHeight.value = window.innerHeight;
        };

        onMounted(() => {
            window.addEventListener('resize', updateViewportSize);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', updateViewportSize);
        });

        return {
            handleClose,
            maxHeight,
            maxWidth,
            modalStyle,
        };
    },
};
</script>
